import { Reducer } from "redux";
import { IDestination } from "../models/destination";
import {
    DestinationZonesActionType,
    SET_DESTINATION_ZONES,
    MERGE_DESTINATION_ZONES,
} from "../actions/destinationsAction";

/**
 * Represents the state of destinations in the Redux store.
 */
export interface DestinationsState {
    data: Map<string, string>;
    names: Map<string, string>;
    currencies: Map<string, string>;
    companyZones: Map<string, string>;
    lastModifyTs?: Map<string, number>;
}

/**
 * The initial state of the destinations reducer.
 */
export const initialState: DestinationsState = {
    data: new Map(),
    names: new Map(),
    currencies: new Map(),
    companyZones: new Map(),
    lastModifyTs: new Map(),
};

/**
 * Sets the destinations based on the payload.
 * @param payload - The array of destinations.
 * @returns The updated destinations state.
 */
const setDestinations = (payload: IDestination[]) => {
    const data = new Map();
    const names = new Map();
    const currencies = new Map();
    const companyZones = new Map();
    const lastModifyTs = new Map();
    payload.forEach(({ id, zone, name, currency, companyId, lastModifyTs: modified }) => {
        data.set(id, zone);
        names.set(id, name);
        currencies.set(id, currency);
        companyZones.set(companyId, zone);
        lastModifyTs.set(id, modified);
    });
    return { data, names, currencies, companyZones, lastModifyTs };
};

/**
 * The reducer function for destination zones.
 * @param state - The current state of destinations.
 * @param action - The action being dispatched.
 * @returns The updated destinations state.
 */
export const destinationZonesReducer: Reducer<DestinationsState, DestinationZonesActionType> = (
    state: DestinationsState = initialState,
    action: DestinationZonesActionType,
): DestinationsState => {
    switch (action.type) {
        case SET_DESTINATION_ZONES:
            return { ...setDestinations(action.payload) };

        case MERGE_DESTINATION_ZONES: {
            const updates = setDestinations(
                action.payload.filter(
                    destination =>
                        !state.lastModifyTs?.has(destination.id) ||
                        destination.lastModifyTs >= (state.lastModifyTs.get(destination.id) ?? 0),
                ),
            );
            return {
                data: new Map([...state.data, ...updates.data]),
                names: new Map([...state.names, ...updates.names]),
                currencies: new Map([...state.currencies, ...updates.currencies]),
                companyZones: new Map([...(state.companyZones ?? []), ...updates.companyZones]),
                lastModifyTs: new Map([...(state.lastModifyTs ?? []), ...updates.lastModifyTs]),
            };
        }

        default:
            return state;
    }
};
